import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package,
  TrendingUp,
  Clock,
  CheckCircle,
  Truck,
  LogOut,
  Download,
  Search,
  Filter,
  Wind,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getAdminToken, removeAdminToken, getAuthHeaders } from '../utils/adminAuth';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      // Fetch orders
      const ordersParams = filter !== 'all' ? `?status=${filter}` : '';
      const ordersResponse = await axios.get(`${API_URL}/api/orders${ordersParams}`, { headers });
      setOrders(ordersResponse.data);

      // Fetch stats
      const statsResponse = await axios.get(`${API_URL}/api/orders/stats/summary`, { headers });
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        removeAdminToken();
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    removeAdminToken();
    navigate('/admin/login');
  };

  const handleDownloadCSV = () => {
    const csvContent = [
      ['Order Number', 'Customer Name', 'Email', 'Phone', 'Total', 'Status', 'Date'].join(','),
      ...filteredOrders.map(order => [
        order.order_number,
        order.customer.name,
        order.customer.email,
        order.customer.phone,
        order.total_amount,
        order.status,
        new Date(order.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `madtoys_orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      order.order_number.toLowerCase().includes(search) ||
      order.customer.name.toLowerCase().includes(search) ||
      order.customer.email.toLowerCase().includes(search) ||
      order.customer.phone.includes(search)
    );
  });

  const getStatusBadge = (status) => {
    const variants = {
      payment_pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      processing: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      shipped: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
      delivered: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' }
    };
    const variant = variants[status] || variants.paid;
    return (
      <Badge className={`${variant.bg} ${variant.text} ${variant.border}`}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="bg-dark-secondary border-gold/10">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">{label}</p>
              <p className="text-3xl font-bold text-white">{value}</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${color}`}>
              <Icon className="w-6 h-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-dark-primary flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
          <Wind className="w-12 h-12 text-gold" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Header */}
      <div className="bg-dark-secondary border-b border-gold/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wind className="w-8 h-8 text-gold" />
              <div>
                <h1 className="text-2xl font-bold text-gold">MADTOYS</h1>
                <p className="text-sm text-gray-400">Admin Dashboard</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gold/20 text-gray-300 hover:text-gold hover:border-gold"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Package}
              label="Total Orders"
              value={stats.total_orders}
              color="bg-blue-500/20 text-blue-400"
            />
            <StatCard
              icon={TrendingUp}
              label="Total Revenue"
              value={`₹${stats.total_revenue.toLocaleString('en-IN')}`}
              color="bg-green-500/20 text-green-400"
            />
            <StatCard
              icon={Clock}
              label="Pending"
              value={stats.by_status.find(s => s._id === 'payment_pending')?.count || 0}
              color="bg-yellow-500/20 text-yellow-400"
            />
            <StatCard
              icon={CheckCircle}
              label="Completed"
              value={stats.by_status.find(s => s._id === 'delivered')?.count || 0}
              color="bg-purple-500/20 text-purple-400"
            />
          </div>
        )}

        {/* Filters & Search */}
        <Card className="bg-dark-secondary border-gold/10 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Search by order number, name, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-dark-primary border-gold/20 text-white"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'paid', 'processing', 'shipped', 'delivered'].map((status) => (
                  <Button
                    key={status}
                    onClick={() => setFilter(status)}
                    variant={filter === status ? 'default' : 'outline'}
                    className={filter === status ? 'bg-gold text-dark-primary' : 'border-gold/20 text-gray-300'}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Button>
                ))}
              </div>
              <Button
                onClick={handleDownloadCSV}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card className="bg-dark-secondary border-gold/10">
          <CardHeader className="border-b border-gold/10">
            <h2 className="text-2xl font-bold text-white">Orders ({filteredOrders.length})</h2>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-dark-primary">
                  <tr>
                    <th className="text-left p-4 text-gray-400 font-semibold">Order #</th>
                    <th className="text-left p-4 text-gray-400 font-semibold">Customer</th>
                    <th className="text-left p-4 text-gray-400 font-semibold">Items</th>
                    <th className="text-left p-4 text-gray-400 font-semibold">Total</th>
                    <th className="text-left p-4 text-gray-400 font-semibold">Status</th>
                    <th className="text-left p-4 text-gray-400 font-semibold">Date</th>
                    <th className="text-left p-4 text-gray-400 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center p-8 text-gray-400">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border-b border-gold/5 hover:bg-dark-primary/50 transition-colors"
                      >
                        <td className="p-4">
                          <span className="font-mono text-gold">{order.order_number}</span>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{order.customer.name}</p>
                            <p className="text-gray-400 text-sm">{order.customer.email}</p>
                            <p className="text-gray-500 text-sm">{order.customer.phone}</p>
                          </div>
                        </td>
                        <td className="p-4 text-gray-300">
                          {order.items.length} item(s)
                        </td>
                        <td className="p-4">
                          <span className="text-gold font-bold">₹{order.total_amount.toLocaleString('en-IN')}</span>
                        </td>
                        <td className="p-4">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="p-4 text-gray-300">
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="p-4">
                          <Button
                            onClick={() => navigate(`/admin/orders/${order.id}`)}
                            size="sm"
                            className="bg-gold/20 hover:bg-gold/30 text-gold border-gold/30"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
